import { computed, ComputedRef } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { RouteName } from "@/apps/merchant3/router/RouteName";
import useConfigurations from "@/apps/merchant3/hooks/useConfigurations";
import MerchantFlags from "@/apps/merchant3/types/MerchantFlags";
import { isDirectToCustomerFlow } from "@/apps/merchant3/helpers/checkoutFlows";
import { getCountryCode } from "@/apps/merchant3/helpers/flowType";

/**
 * Maps backend checkout step names to frontend route names for configurable M3 flow.
 */
const stepToRouteMap: Record<string, RouteName> = {
  POLICY: RouteName.M3ConfigurablePolicy,
  KYC: RouteName.M3ConfigurableKyc,
  CREDIT_CHECK: RouteName.M3ConfigurableCreditCheck,
  CUSTOMER_CONTACT_INFO: RouteName.M3ConfigurableCustomerContactInfo,
  CONTRACT: RouteName.M3Contract,
  SOFTWARE_INSTALLATION: RouteName.M3Software,
  RECEIPT: RouteName.M3Receipt,
};

/**
 * List of route names that are part of the configurable M3 flow.
 */
const configurableRouteNames = new Set<RouteName>([
  RouteName.M3ConfigurablePolicy,
  RouteName.M3ConfigurableKyc,
  RouteName.M3ConfigurableCreditCheck,
  RouteName.M3ConfigurableDob,
  RouteName.M3ConfigurableIdNumberDob,
  RouteName.M3ConfigurableCustomerContactInfo,
  RouteName.M3ConfigurableOffers,
  RouteName.M3ConfigurableOffersV2,
]);

type ComposableResult = {
  navigateToNextStep: () => Promise<void>;
  navigateToNextStepWithRefresh: (currentStep: string) => Promise<void>;
  getRouteForStep: (step: string) => RouteName | null;
  isConfigurableFlow: ComputedRef<boolean>;
};

/**
 * Hook for navigating between configurable M3 checkout steps using backend-provided nextStep.
 */
export function useConfigurableNavigation(): ComposableResult {
  const router = useRouter();
  const route = useRoute();
  const store = useStore();
  const { nextStep, loadConfigs } = useConfigurations();

  /**
   * Check if current route is part of the configurable M3 flow.
   */
  const isConfigurableFlow = computed(() => {
    const currentRouteName = route.name as RouteName;
    return (
      configurableRouteNames.has(currentRouteName) ||
      route.meta?.isConfigurable === true
    );
  });

  /**
   * Get the appropriate offers route based on merchant flags.
   * Returns configurable route for configurable flow, otherwise falls back to standard routes.
   */
  const getOffersRouteForConfigurable = (): RouteName => {
    const hasNewOffersUi = store.state.merchant?.merchantFlags?.includes(
      MerchantFlags.NEW_OFFERS_UI
    );

    // For Direct to Customer flow, use the DTC route (not configurable)
    if (isDirectToCustomerFlow.value) {
      return RouteName.DirectToCustomerOfferSummary;
    }

    // For configurable flow, use configurable offer routes
    return hasNewOffersUi
      ? RouteName.M3ConfigurableOffersV2
      : RouteName.M3ConfigurableOffers;
  };

  const getRouteForStep = (step: string): RouteName | null => {
    // Special handling for OFFERS step - use dynamic route based on merchant flags
    if (step === "OFFERS") {
      return getOffersRouteForConfigurable();
    }

    return stepToRouteMap[step] || null;
  };

  /**
   * Navigate to the next step using the cached nextStep from configuration.
   */
  const navigateToNextStep = async (): Promise<void> => {
    if (!nextStep.value) {
      console.warn("No next step available from configuration");
      return;
    }

    const route = getRouteForStep(nextStep.value);

    if (route) {
      router.push({ name: route, params: { countryCode: getCountryCode() } });
    } else {
      console.warn(`Unknown step: ${nextStep.value}`);
    }
  };

  /**
   * Reload configuration from backend and then navigate to the next step.
   * Use this after cart state changes (e.g., after DOB match adds SKIPPED_KYC).
   */
  const navigateToNextStepWithRefresh = async (
    currentStep: string
  ): Promise<void> => {
    const cartTag = store.state.carts?.cartTag;

    if (!cartTag) {
      console.warn("No cart tag available");
      return;
    }

    // Reload configuration to get updated nextStep based on new cart state
    await loadConfigs(cartTag, currentStep);

    // Now navigate using the refreshed nextStep
    await navigateToNextStep();
  };

  return {
    navigateToNextStep,
    navigateToNextStepWithRefresh,
    getRouteForStep,
    isConfigurableFlow,
  };
}
